import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, KeyRound, Loader2 } from "lucide-react";
import { Fragment } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, ApiError } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { qk } from "@/lib/query/keys";
import type { ClaudeAccountsView, ClaudeSignInFailure } from "@/lib/types";

const select = (r: { accounts: ClaudeAccountsView | null }) => r.accounts;
const label = (name: string) => (name === "default" ? "Default login" : name);

/**
 * Settings → Claude account (admins): which login agent runs sign in as. The
 * accounts are on the host (`claude-account`), so this shows the names the worker
 * last published and asks the worker to switch. The worker proves the account signs
 * in first (`claude-account check`, a few seconds), so a choice reads "Testing…" and
 * then either "In use" or the reason it was refused, with the current account kept
 * and a way to switch without the test.
 * Tokens never reach the browser — there is nothing here to add one; that stays a
 * command on the host.
 */
export function ClaudeAccountSettings() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: qk.claudeAccounts(),
    queryFn: api.claudeAccounts,
    select,
    // Quick while a switch is on its way, slower otherwise: the CLI can switch too.
    refetchInterval: (q) => (q.state.data?.accounts?.pending || q.state.data?.accounts?.checking ? 1_500 : 10_000),
  });
  const switchTo = useMutation({
    mutationFn: ({ name, force }: { name: string; force?: boolean }) => api.setClaudeAccount(name, force),
    onSuccess: (result) => client.setQueryData(qk.claudeAccounts(), result),
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not switch the account"),
  });

  if (query.isPending) return <p className="text-[13px] text-muted-foreground">Loading…</p>;
  const view = query.data;
  if (!view) {
    return (
      <p className="text-[13px] text-muted-foreground">
        The worker has not reported the host's accounts yet. It does once it is running on the host.
      </p>
    );
  }

  const pending = switchTo.isPending ? switchTo.variables.name : (view.pending?.name ?? view.checking?.name);
  const testing = !switchTo.isPending && !view.pending && view.checking ? view.checking.name : null;
  // A refusal is news until the next switch; one for the account now in use is not.
  const failed = view.lastSwitch && !view.lastSwitch.ok && view.lastSwitch.name !== view.active ? view.lastSwitch : null;
  const onlyDefault = view.accounts.length === 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={pending ?? view.active}
          onValueChange={(name) => name !== view.active && switchTo.mutate({ name })}
          disabled={view.stale || Boolean(pending)}
        >
          <SelectTrigger className="w-full win-sm:w-64" aria-label="Claude account">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {view.accounts.map((account) => (
              <SelectItem key={account.name} value={account.name}>
                {label(account.name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {pending ? (
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" /> {testing ? `Testing ${label(pending)}…` : `Switching to ${label(pending)}…`}
          </span>
        ) : (
          <span className="text-[12.5px] text-muted-foreground">
            In use: <span className="font-medium text-foreground">{label(view.active)}</span>
          </span>
        )}
      </div>
      <p className="text-[12.5px] text-muted-foreground">Applies to new agent runs; running ones finish on their current account.</p>
      <SignInFailureBanner failure={view.signInFailure ?? null} />
      {failed && !pending ? (
        <div className="flex items-start gap-1.5 text-[12.5px] text-destructive">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <p className="min-w-0 [overflow-wrap:anywhere]">
            Did not switch to <b>{label(failed.name)}</b> {relativeTime(failed.at)}, so runs stay on {label(view.active)}:{" "}
            <CodeText text={(failed.error ?? "it was refused").replace(new RegExp(`^${failed.name}: `), "")} />{" "}
            <button
              type="button"
              className="whitespace-nowrap text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => switchTo.mutate({ name: failed.name, force: true })}
              disabled={view.stale}
            >
              Switch without testing
            </button>
          </p>
        </div>
      ) : null}
      {view.stale ? (
        <p className="flex items-start gap-1.5 text-[12.5px] text-[var(--waiting)]">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          The worker last reported {relativeTime(view.observedAt)}, so a switch cannot be applied right now.
        </p>
      ) : null}
      {onlyDefault ? (
        <p className="text-[12.5px] text-muted-foreground">
          Only the default login is set up. Add another on the host with <code className="rounded bg-muted px-1">claude-account add &lt;name&gt;</code>.
        </p>
      ) : null}
    </div>
  );
}

/** Text with `backticked` spans drawn as code: the CLI's messages name commands that way. */
function CodeText({ text }: { text: string }) {
  return (
    <>
      {text.split(/`([^`]+)`/).map((part, i) =>
        i % 2 ? (
          <code key={i} className="rounded bg-muted px-1 text-foreground">
            {part}
          </code>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * Runs refused at sign-in on one account (a bad, expired or revoked token): loud,
 * because otherwise it is one ordinary-looking failure per agent chat. Shown in
 * Settings → Claude account and on Status until the account is fixed or switched.
 */
export function SignInFailureBanner({ failure, className }: { failure: ClaudeSignInFailure | null; className?: string }) {
  if (!failure) return null;
  const named = failure.account !== "default";
  return (
    <div
      role="alert"
      className={`flex items-start gap-2.5 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2.5 text-[12.5px] ${className ?? ""}`}
    >
      <KeyRound className="mt-0.5 size-4 shrink-0 text-destructive" />
      <div className="min-w-0 space-y-0.5 [overflow-wrap:anywhere]">
        <p className="font-semibold text-destructive">
          {failure.count} agent run{failure.count === 1 ? "" : "s"} refused at sign-in on {label(failure.account)}
          <span className="font-normal text-muted-foreground"> · since {relativeTime(failure.firstAt)}</span>
        </p>
        <p className="text-muted-foreground">{failure.message}</p>
        <p>
          {named ? (
            <>
              Switch to another account here, or re-add it on the host with{" "}
              <code className="rounded bg-muted px-1">claude-account add {failure.account}</code>. The refused turns are queued again
              once it is fixed.
            </>
          ) : (
            <>
              The host's own login was refused: sign in again on the host (<code className="rounded bg-muted px-1">claude /login</code>), or
              switch to another account here.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
